import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { useCreateMitigation, useEditMitigation } from "../mutation";
import { getScore } from "../utils/getScore";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { raiseRiskApi } from "../api/raiseRiskApi";

export const useMitigation = ({ riskId, refetch }: { riskId: string | null, refetch: () => void }) => {

    const { getFilterOptions } = useDisplayField([]);
    const riskOptions = getFilterOptions("RISK_SCORE");
    const normalizedId = riskId === 'null' ? null : riskId;

    const { data: mitigationData, isFetching: isMitigationFetching, isPending: isMitigationPending, refetch: refetchMitigations } = useQuery({
        queryKey: ["project-risk-mitigation", normalizedId,],
        queryFn: () => raiseRiskApi.fetchMitigations({ riskId: String(normalizedId) }),
        enabled: !!normalizedId
    });

    const createMitigation = useCreateMitigation();
    const editMitigation = useEditMitigation();

    const [openMitigation, setOpenMitigation] = useState(false);

    const [mitigationsData, setMitigationsData] = useState<{
        impactCategory: null | string,
        impactTitle: null | string,
        impactDescription: null | string,
        impactOwner: null | string,
        impact: number,
        probability: number,
        impactScore: string,
        probabilityScore: string,
        impProbScore: number
    }>({
        impactCategory: null,
        impactTitle: null,
        impactDescription: null,
        impactOwner: null,
        impact: 1,
        probability: 1,
        impactScore: 'low',
        probabilityScore: 'low',
        impProbScore: 1
    });

    const [editMitigationStatus, setEditMitigationStatus] = useState<{
        editMitigationStatus: boolean
        mitigationId: string | null
    }>({
        editMitigationStatus: false,
        mitigationId: null
    });

    const onSaveMitigation = async () => {
        const payload = {
            "impact" : mitigationsData.impactCategory,
            "title": mitigationsData.impactTitle,
            "description": mitigationsData.impactDescription,
            "owner": mitigationsData.impactOwner,
            "residual_impact": getScore(mitigationsData.impact, riskOptions),
            "residual_probability": getScore(mitigationsData.probability, riskOptions),
            "residual_impact_score": mitigationsData.impact,
            "residual_probability_score": mitigationsData.probability,
            "imp_prob_score": mitigationsData.probability * mitigationsData.impact,
        };
        if (editMitigationStatus.editMitigationStatus) {
            await editMitigation.mutateAsync({ riskId: String(normalizedId), mitigationId: editMitigationStatus.mitigationId!, data: payload });
        } else {
            await createMitigation.mutateAsync({ riskId: String(normalizedId), data: payload });
        };
        await refetchMitigations();
        onClearMitigation();
        refetch();
    };

    const onEditMitigation = async (data: any) => {
        setOpenMitigation(true);
        setEditMitigationStatus({
            editMitigationStatus: true,
            mitigationId: data.id
        });
        setMitigationsData({
            impactCategory: data.impact,
            impactTitle: data.title,
            impactDescription: data.description,
            impactOwner: data.owner,
            impact: data.residual_impact_score,
            probability: data.residual_probability_score,
            impactScore: data.residual_impact,
            probabilityScore: data.imp_prob_score,
            impProbScore: data.residual_probability_score
        });
    };

    const onClearMitigation = async () => {
        setOpenMitigation(false);
        setEditMitigationStatus({
            editMitigationStatus: false,
            mitigationId: null
        });
        setMitigationsData({
            impactCategory: null,
            impactTitle: null,
            impactDescription: null,
            impactOwner: null,
            impact: 1,
            probability: 1,
            impactScore: 'low',
            probabilityScore: 'low',
            impProbScore: 1
        });
    };

    return {
        openMitigation, setOpenMitigation,
        mitigationsData, setMitigationsData,
        editMitigationStatus, onSaveMitigation, onEditMitigation, onClearMitigation,
        mitigationData: mitigationData?.data?.rows, isMitigationFetching, isMitigationPending, refetchMitigations,
        isSaving: createMitigation.isPending || editMitigation.isPending
    }

};